import access from "csi/access/_base";
import def from "csi/def";
import { View } from "csi/def/View";
import html from "csi/html";
import reflection from "csi/reflection";
import { resolveNode } from "csi/service/nodeService";
import { getViewAccessRights } from "csi/service/viewService";
import { routerStore } from "csi/state/rootStore";
import Cache from "csi/store/Cache";
import view from "csi/view";
import { ViewWidget } from "csi/view/Widget";
import React, { CSSProperties, FunctionComponent, useCallback, useRef } from "react";

export const CSS_DOJO_VIEW = "csi-dojo-view";

async function createView(props: DojoViewProps) {
	const {
		forceReadOnly,
		fullHeight,
		nodeUrl,
		refreshTimestamp,
		subtypeUrl,
		viewId,
		viewNodeType,
		viewParams,
	} = props;

	const { locationType } = routerStore;

	const {
		cacheView: cacheViewFromViewParams,
		defView: defViewFromViewParams,
		flexHeight,
		...otherProps
	} = viewParams || {};

	const ownCache = !cacheViewFromViewParams;

	let cacheView = cacheViewFromViewParams || new Cache();

	// check to see if node already cached, and if not, then fetch it
	let node = cacheView.get(nodeUrl);
	if (!node) {
		node = await resolveNode(nodeUrl, cacheView);
	}

	// find view definition if not given
	const type = viewNodeType || node?.$Type;
	const defView = defViewFromViewParams || def.getClassViewDef(reflection.getClass(type), viewId) as View;
	if (!defView) {
		return null;
	}
	const subtypeName = reflection.getSubtypeName(subtypeUrl);
	// ensure user has access to the view
	const accessRight = getViewAccessRights(locationType, defView.getTypeNode(), viewId, subtypeName, node);

	if (forceReadOnly || (accessRight == access.RESOURCE_READ_ONLY_ACCESS)) {
		// set view read only
		defView.setReadOnly(true);
	} else if (!accessRight) {
		// return null if no access
		return null;
	}

	// recreate cache for view if it requires its own cache
	if (defView.bOwnCache && !ownCache) {
		// NOTE: mirrorCache is not set in this case, which means any changes in this view will not get reflected
		// in the rest of the page; this may only be an issue if names of nodes in the breadcrumb are changed, but
		// that should hopefully not be an issue with views using OwnCache="1". Normally they are table views showing
		// other data.
		//
		// It may be ok to use a mirrorCache, but initially we should try to do without it to keep nodes from view
		// leaking into other views.
		cacheView = new Cache();
	}

	return view.newView(defView, node, {
		cacheView,
		flexHeight: flexHeight || fullHeight,
		refreshTimestamp,
		urlSubtype: subtypeUrl,
		...otherProps,
	});
}

export interface DojoViewProps {
	/** CSS class to apply to div surrounding view */
	className?: string;

	/** force view to read-only */
	forceReadOnly?: boolean;

	/** counter used to force view refresh */
	forceRefresh?: number;

	/** indicate if view should occupy full height of container */
	fullHeight?: boolean;

	/** indicate if view should set the min height during a refresh (using the height from previous view height) */
	maintainHeightOnRefresh?: boolean;

	/** root node url of view (defaults to current location node) */
	nodeUrl: string;

	/** callback function to call once view is first created */
	onViewCreated?: (view: ViewWidget) => void;

	/** callback function to call once view is first loaded */
	onViewLoad?: (view: ViewWidget) => void;

	/** timestamp used by view cache to determine if nodes are stale */
	refreshTimestamp?: number;

	/** CSS styling to apply to root element */
	style?: CSSProperties;

	/** subtype url used to instantiate the view, if any */
	subtypeUrl?: string;

	/** switchboard action for view */
	switchboard?: object;

	/** owner type of switchboard */
	switchboardOwnerType?: string;

	/** view id to render */
	viewId: string;

	/** node type where view is defined (defaults to root node type) */
	viewNodeType?: string;

	/** optional parameters for view */
	viewParams?: Record<string, any>;
}

const DojoView: FunctionComponent<DojoViewProps> = (props) => {
	const {
		className,
		forceRefresh,
		fullHeight,
		maintainHeightOnRefresh,
		nodeUrl,
		onViewCreated,
		onViewLoad,
		refreshTimestamp,
		style,
		subtypeUrl,
		switchboard,
		switchboardOwnerType,
		viewId,
		viewNodeType,
		viewParams,
	} = props;

	const viewRef = useRef<ViewWidget | null>();
	const divStyle = { ...style };
	const viewHeight = useRef(0);

	if (fullHeight) {
		divStyle.height = "100%";
	}

	if (maintainHeightOnRefresh) {
		divStyle.minHeight = viewHeight.current;
	}

	// don't monitor props.viewParams for changes as it is an object and could be different every time
	// even if it contains the same properties and values
	const contentRef = useCallback(async (domNode: HTMLElement) => {
		if (domNode) {
			const view = viewRef.current = await createView(props);

			html.clear(domNode);

			if (view) {
				if (switchboard) {
					view.addSwitchboard(switchboard, switchboardOwnerType);
				}

				view.placeAt(domNode);
				onViewCreated?.(view);

				Promise.all([view.startup()])
					.then(() => {
						if (view.isValidView()) {
							viewHeight.current = view.domNode.clientHeight;
							return onViewLoad?.(view);
						}
					});
			}
		} else if (viewRef.current) {
			viewRef.current.destroyView();
			viewRef.current = null;
		}
	}, [forceRefresh, nodeUrl, refreshTimestamp, subtypeUrl, viewId, viewNodeType, viewParams?.defView?.getKey()]);

	return (
		<div
			className={`${CSS_DOJO_VIEW} ${className || ""}`}
			ref={contentRef}
			style={divStyle}
		/>
	);
};

export default DojoView;